import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, ChevronDown, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* =============================================================
   Form primitives — premium dark inputs with soft accent glow ring
   ============================================================= */

const labelCls =
    "block text-[12px] font-medium uppercase tracking-widest-2 text-text-secondary";

const baseInputCls =
    "mt-2 w-full rounded-lg border border-border-subtle bg-[var(--bg-elevated-2)] px-4 py-3.5 text-[15px] text-text-primary placeholder:text-text-muted transition-all duration-200 ease-luxury focus:outline-none focus:border-[rgba(124,255,79,0.55)] focus:ring-[3px] focus:ring-[rgba(124,255,79,0.15)] disabled:opacity-50";

const errorCls = "mt-2 text-[12.5px] text-red-400/90";

export const FormField = ({
    label,
    name,
    type = "text",
    value,
    onChange,
    required,
    placeholder,
    error,
    "data-testid": testId,
}) => (
    <div>
        <label htmlFor={name} className={labelCls}>
            {label}
            {required && <span className="text-[var(--accent)] ml-1">*</span>}
        </label>
        <input
            id={name}
            name={name}
            type={type}
            value={value}
            onChange={(e) => onChange(name, e.target.value)}
            placeholder={placeholder}
            required={required}
            className={baseInputCls}
            data-testid={testId}
            aria-invalid={!!error}
        />
        {error && <p className={errorCls}>{error}</p>}
    </div>
);

export const FormTextarea = ({
    label,
    name,
    value,
    onChange,
    required,
    placeholder,
    error,
    "data-testid": testId,
    rows = 4,
}) => (
    <div>
        <label htmlFor={name} className={labelCls}>
            {label}
            {required && <span className="text-[var(--accent)] ml-1">*</span>}
        </label>
        <textarea
            id={name}
            name={name}
            value={value}
            onChange={(e) => onChange(name, e.target.value)}
            placeholder={placeholder}
            required={required}
            rows={rows}
            className={`${baseInputCls} min-h-[120px] resize-y`}
            data-testid={testId}
            aria-invalid={!!error}
        />
        {error && <p className={errorCls}>{error}</p>}
    </div>
);

export const FormSelect = ({
    label,
    name,
    value,
    onChange,
    required,
    options = [],
    placeholder = "Select…",
    error,
    "data-testid": testId,
}) => (
    <div>
        <label htmlFor={name} className={labelCls}>
            {label}
            {required && <span className="text-[var(--accent)] ml-1">*</span>}
        </label>
        <div className="relative">
            <select
                id={name}
                name={name}
                value={value}
                onChange={(e) => onChange(name, e.target.value)}
                required={required}
                className={`${baseInputCls} appearance-none pr-12 ${
                    !value ? "text-text-muted" : ""
                }`}
                data-testid={testId}
                aria-invalid={!!error}
            >
                <option value="" disabled>
                    {placeholder}
                </option>
                {options.map((opt) => (
                    <option
                        key={opt}
                        value={opt}
                        className="bg-[var(--bg-elevated-2)] text-text-primary"
                    >
                        {opt}
                    </option>
                ))}
            </select>
            <ChevronDown
                aria-hidden
                className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
                strokeWidth={1.5}
            />
        </div>
        {error && <p className={errorCls}>{error}</p>}
    </div>
);

/* =============================================================
   Success state — replaces the form
   ============================================================= */

export const FormSuccess = ({
    title = "Received.",
    message = "Thank you. We'll be in touch within one business day.",
    "data-testid": testId = "form-success",
}) => (
    <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col items-center px-8 py-16 text-center"
        data-testid={testId}
        role="status"
        aria-live="polite"
    >
        <div
            className="flex h-14 w-14 items-center justify-center rounded-full border border-[rgba(124,255,79,0.35)] bg-[rgba(124,255,79,0.06)]"
            style={{ boxShadow: "0 0 40px rgba(124,255,79,0.15)" }}
        >
            <Check
                className="h-6 w-6 text-[var(--accent)]"
                strokeWidth={2}
            />
        </div>
        <h3 className="mt-8 font-display text-[clamp(24px,3vw,32px)] font-semibold tracking-tight-2 text-text-primary">
            {title}
        </h3>
        <p className="mt-3 max-w-md text-[15.5px] leading-[1.65] text-text-secondary">
            {message}
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
                to="/"
                data-testid={`${testId}-back-home`}
                className="inline-flex items-center gap-2 rounded-full border border-border-default px-6 py-2.5 text-[14px] font-medium text-text-primary transition-all duration-300 ease-luxury hover:bg-[var(--bg-elevated)] hover:border-[rgba(124,255,79,0.4)]"
            >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Back to Home
            </Link>
            <Link
                to="/divisions"
                data-testid={`${testId}-divisions`}
                className="inline-flex items-center gap-2 rounded-full border border-border-default px-6 py-2.5 text-[14px] font-medium text-text-primary transition-all duration-300 ease-luxury hover:text-[var(--accent)] hover:border-[rgba(124,255,79,0.5)] hover:bg-[rgba(124,255,79,0.04)]"
            >
                Explore Divisions
            </Link>
        </div>
    </motion.div>
);

/* =============================================================
   Validation helper
   ============================================================= */

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (values, fields) => {
    const errors = {};
    for (const f of fields) {
        const v = (values[f.name] || "").trim();
        if (f.required && !v) {
            errors[f.name] = "Required";
            continue;
        }
        if (f.type === "email" && v && !emailRe.test(v)) {
            errors[f.name] = "Please enter a valid email.";
        }
    }
    return errors;
};

/* =============================================================
   useLeadSubmit — submit hook used by all 5 lead forms
   ============================================================= */

export const useLeadSubmit = ({ endpoint, fields }) => {
    const initialValues = Object.fromEntries(fields.map((f) => [f.name, ""]));
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const onChange = (name, value) => {
        setValues((v) => ({ ...v, [name]: value }));
        setErrors((e) => {
            if (!e[name]) return e;
            const { [name]: _, ...rest } = e;
            return rest;
        });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const v = validate(values, fields);
        setErrors(v);
        if (Object.keys(v).length > 0) return;

        setSubmitting(true);
        try {
            const payload = Object.fromEntries(
                Object.entries(values).map(([k, val]) => [k, val.trim()]),
            );
            await axios.post(`${API}${endpoint}`, payload);
            setSuccess(true);
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            const message =
                typeof detail === "string"
                    ? detail
                    : "Please review your entries and try again.";
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return { values, errors, submitting, success, onChange, onSubmit };
};

/* =============================================================
   FormCard wrapper — premium card surrounding the form
   ============================================================= */

export const FormCard = ({ children, className = "", "data-testid": testId }) => (
    <div
        className={`relative overflow-hidden rounded-2xl border border-border-subtle bg-elevated p-8 sm:p-10 ${className}`}
        data-testid={testId}
        style={{
            boxShadow:
                "0 24px 48px -24px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.01)",
        }}
    >
        {children}
    </div>
);
